/* eslint-disable no-console */
import loglevel from 'loglevel';

const getRuntime = (): 'server' | 'browser' => {
  if (typeof globalThis.window !== 'undefined') return 'browser';
  return 'server';
};

const runtime = getRuntime();

const formatMessage = (message: string, optionalParams: any[]): string => {
  return optionalParams.length > 0 ? `${message} ${optionalParams.map((p) => JSON.stringify(p)).join(' ')}` : message;
};

const getTimeString = (): string => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  return `${hours}:${minutes}:${seconds}.${milliseconds}`;
};

const writeLog = (level: 'trace' | 'debug' | 'info' | 'warn' | 'error', message: string, ...optionalParams: any[]) => {
  // Check if logging is enabled for this level before evaluating lazy parameters
  const shouldLog =
    runtime === 'browser'
      ? loglevel.getLevel() <= loglevel.levels[level.toUpperCase() as keyof typeof loglevel.levels]
      : true; // Always log on server for now

  if (!shouldLog) {
    return; // Skip logging entirely if disabled
  }

  // Evaluate lazy parameters (functions) only when logging is enabled
  const evaluatedParams = optionalParams.map((param) => (typeof param === 'function' ? param() : param));

  if (runtime === 'browser') {
    // Add timestamp prefix if trace level is active (show timestamps on ALL messages when trace is enabled)
    const isTraceActive = loglevel.getLevel() <= loglevel.levels.TRACE;
    const timePrefix = isTraceActive ? `[${getTimeString()}] ` : '';
    const prefixedMessage = timePrefix + message;

    switch (level) {
      case 'trace':
        loglevel.trace(prefixedMessage, ...evaluatedParams);
        break;
      case 'debug':
        loglevel.debug(prefixedMessage, ...evaluatedParams);
        break;
      case 'info':
        loglevel.info(prefixedMessage, ...evaluatedParams);
        break;
      case 'warn':
        loglevel.warn(prefixedMessage, ...evaluatedParams);
        break;
      case 'error':
        loglevel.error(prefixedMessage, ...evaluatedParams);
        break;
    }
  } else {
    // Server-side logging. winston or console, depending on initialization above
    const formattedMessage = formatMessage(message, evaluatedParams);
    const prefix = `[${new Date().toISOString()}]`;
    switch (level) {
      case 'trace':
        console.trace(prefix, formattedMessage);
        break;
      case 'debug':
        console.debug(prefix, formattedMessage);
        break;
      case 'info':
        console.info(prefix, formattedMessage);
        break;
      case 'warn':
        console.warn(prefix, formattedMessage);
        break;
      case 'error':
        console.error(prefix, formattedMessage);
        break;
    }
  }
};

export const log = {
  debug: (message: string, ...optionalParams: any[]) => writeLog('debug', message, ...optionalParams),
  trace: (message: string, ...optionalParams: any[]) => writeLog('trace', message, ...optionalParams),
  info: (message: string, ...optionalParams: any[]) => writeLog('info', message, ...optionalParams),
  warn: (message: string, ...optionalParams: any[]) => writeLog('warn', message, ...optionalParams),
  error: (message: string, ...optionalParams: any[]) => writeLog('error', message, ...optionalParams),
};
